import { HOST_NAME } from './url'

/**
 * Image transformation options for Cloudflare Worker images
 *
 * NOTE: For user-uploaded images, DO NOT construct URLs client-side.
 * Instead, use the GraphQL `transformUrl` field on the Image type which
 * provides signed URLs that prevent unauthorized bandwidth usage.
 *
 * Example GraphQL usage:
 * ```graphql
 * fragment MyComponent_image on Image {
 *   transformUrl(transform: { w: 100, h: 100, dpr: 3, fo: "auto", fit: "cover" })
 * }
 * ```
 */
export type ImageTransformOptions = {
  /** Width in pixels (before dpr scaling) */
  w?: number
  /** Height in pixels (before dpr scaling) */
  h?: number
  /** Device pixel ratio (1-3), defaults to 3 */
  dpr?: number
  /** Focus/gravity for cropping: auto, left, right, center, top, bottom, face */
  fo?: 'auto' | 'left' | 'right' | 'center' | 'top' | 'bottom' | 'face'
  /** Fit mode: scale-down, contain, cover, crop, pad */
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad'
  /** Quality (1-100), defaults to 85 */
  quality?: number
  /** Output format */
  format?: 'image/webp' | 'image/avif' | 'image/png' | 'image/jpeg'
  /** Background color for padding (hex without #, e.g., 'ffffff') */
  bg?: string
}

/**
 * Builds query string from transform options
 */
function buildQueryString(options: ImageTransformOptions): string {
  const params = new URLSearchParams()

  if (options.w !== undefined) params.set('w', String(options.w))
  if (options.h !== undefined) params.set('h', String(options.h))
  if (options.dpr !== undefined) params.set('dpr', String(options.dpr))
  if (options.fo) params.set('fo', options.fo)
  if (options.fit) params.set('fit', options.fit)
  if (options.quality !== undefined) params.set('quality', String(options.quality))
  if (options.format) params.set('format', options.format)
  if (options.bg) params.set('bg', options.bg)

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Construct a URL for a static asset with transformations
 *
 * Static assets (backgrounds, default avatars, icons) do not require
 * signed URLs since they are not user-uploaded content.
 *
 * @param path - Path to the asset (e.g., 'icons/calendar.png' or 'social-card-backgrounds/purple-yellow.png')
 * @param options - Transform options
 * @returns Full URL to the static asset
 *
 * @example
 * // Icon with transformations
 * getStaticImageUrl('icons/calendar.png', { w: 32, h: 32, dpr: 3 })
 * // => "https://guild.host/i/static/icons/calendar.png?w=32&h=32&dpr=3"
 *
 * @example
 * // Background without transformations
 * getStaticImageUrl('social-card-backgrounds/purple-yellow.png')
 * // => "https://guild.host/i/static/social-card-backgrounds/purple-yellow.png"
 */
export function getStaticImageUrl(
  path: string,
  options: ImageTransformOptions = {}
): string {
  const query = buildQueryString(options)
  return `${HOST_NAME}/i/static/${path}${query}`
}

/**
 * Default avatar filenames for users without a photo
 */
export const DEFAULT_USER_AVATARS = [
  'default-avatar-1.png',
  'default-avatar-2.png',
  'default-avatar-3.png',
  'default-avatar-4.png',
]

/**
 * Get a deterministic default avatar URL based on user ID
 */
export function getDefaultAvatarUrl(
  userId: string,
  options: ImageTransformOptions = {}
): string {
  // Use hash of userId to pick a consistent avatar
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = (hash << 5) - hash + userId.charCodeAt(i)
    hash |= 0
  }
  const index = Math.abs(hash) % DEFAULT_USER_AVATARS.length
  const avatar = DEFAULT_USER_AVATARS[index]
  return getStaticImageUrl(`avatars/${avatar}`, { dpr: 3, fo: 'auto', ...options })
}

/**
 * Social card background URLs
 */
export const SOCIAL_CARD_BACKGROUNDS = {
  purpleYellow: 'social-card-backgrounds/purple-yellow.png',
  pinkYellow: 'social-card-backgrounds/pink-yellow.png',
  bluePurple: 'social-card-backgrounds/blue-purple.png',
  blueTeal: 'social-card-backgrounds/blue-teal.png',
  purpleBlue: 'social-card-backgrounds/purple-blue.png',
} as const

/**
 * Get social card background URL with transformations
 */
export function getSocialCardBackgroundUrl(
  background: keyof typeof SOCIAL_CARD_BACKGROUNDS | string,
  options: ImageTransformOptions = {}
): string {
  const path =
    background in SOCIAL_CARD_BACKGROUNDS
      ? SOCIAL_CARD_BACKGROUNDS[background as keyof typeof SOCIAL_CARD_BACKGROUNDS]
      : background
  return getStaticImageUrl(path, options)
}
